// Modal para activar manualmente la fase BLIND_ACTIVE
// Usado cuando hay ofertas amarillas/rojas que requieren aprobación manual
import React, { useState, useEffect } from 'react';
import { X, Play, EyeOff, Clock, Calendar } from 'lucide-react';
import { SemaphoreBadge } from '../SemaphoreIndicator';

// Opciones de duración en horas para oferta ciega
const DURATION_OPTIONS = [1, 2, 6, 12, 24, 36, 48, 72];

const ActivateBlindActiveModal = ({
  property,
  blindOffers = [],
  onConfirm,
  onSkip,
  onCancel
}) => {
  // Estado para inicio ahora
  const [startNow, setStartNow] = useState(true);

  // Estado para la fecha/hora de inicio
  const [startDateTime, setStartDateTime] = useState('');

  // Estado para la fecha/hora de cierre
  const [endDateTime, setEndDateTime] = useState('');

  const [showActivateWarning, setShowActivateWarning] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  const verifiedOffers = blindOffers.filter(offer => offer.isVerified);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Función para formatear fecha a datetime-local
  const formatDatetimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Función para redondear a la hora siguiente
  const roundToNextHour = (date) => {
    const rounded = new Date(date);
    rounded.setMinutes(0, 0, 0);
    if (date.getMinutes() > 0 || date.getSeconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    return rounded;
  };

  // Función para calcular fecha de cierre basada en inicio + horas
  const calculateEndDateTime = (startDate, hours) => {
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + hours);
    return endDate;
  };

  // Inicializar fechas al montar
  useEffect(() => {
    const now = new Date();
    const startDefault = roundToNextHour(now);
    setStartDateTime(formatDatetimeLocal(startDefault));

    // Por defecto: 24 horas desde ahora, redondeado a la hora siguiente
    const endDefault = calculateEndDateTime(roundToNextHour(now), 24);
    setEndDateTime(formatDatetimeLocal(endDefault));
  }, []);

  // Handler para botones de duración
  const handleDurationClick = (hours) => {
    const baseDate = startNow ? roundToNextHour(new Date()) : new Date(startDateTime);
    const endDate = calculateEndDateTime(baseDate, hours);
    setEndDateTime(formatDatetimeLocal(endDate));
    setShowActivateWarning(false);
    setShowCloseWarning(false);
  };

  // Obtener fecha efectiva de inicio
  const getEffectiveStartDate = () => startNow ? new Date() : new Date(startDateTime);

  // Calcular duración actual para mostrar
  const getCurrentDuration = () => {
    if (!endDateTime) return 24;
    const start = getEffectiveStartDate();
    const end = new Date(endDateTime);
    const hours = Math.round((end - start) / (1000 * 60 * 60));
    return isNaN(hours) ? 24 : hours;
  };

  // Verificar si la fecha es muy próxima (menos de 12 horas)
  const isShortDuration = () => {
    return getCurrentDuration() < 12;
  };

  const handleConfirm = () => {
    // Si la duración es menor a 12h y no se ha mostrado el aviso, mostrar aviso
    if (isShortDuration() && !showActivateWarning) {
      setShowActivateWarning(true);
      setShowCloseWarning(false);
      return;
    }
    // Pasar el deadline exacto en lugar de la duración
    const deadlineDate = new Date(endDateTime);
    onConfirm(deadlineDate);
  };

  const handleClose = () => {
    // Primera vez: mostrar aviso
    if (!showCloseWarning) {
      setShowCloseWarning(true);
      setShowActivateWarning(false);
      return;
    }
    // Segunda vez: ejecutar
    onSkip();
  };

  // Formatear duración restante en horas y minutos
  const formatRemainingTime = () => {
    const hours = getCurrentDuration();
    return `${hours} horas`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal Container - Fullscreen móvil/tablet, centrado desktop */}
      <div className="fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4">
        <div
          className="relative bg-slate-800 w-full h-full flex flex-col border-slate-700 lg:rounded-2xl lg:shadow-2xl lg:max-w-2xl lg:h-auto lg:border lg:max-h-[95vh] border-t border-purple-500/30 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header compacto */}
          <div className="p-3 lg:p-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="bg-purple-500/20 p-1.5 rounded-lg">
                  <EyeOff className="w-4 h-4 lg:w-5 lg:h-5 text-purple-400" />
                </div>
                <h2 className="text-sm lg:text-base font-bold text-white">
                  Activar contador final de ofertas ciegas
                </h2>
              </div>
              <button
                onClick={onCancel}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Propiedad y precio en línea */}
            <div className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2">
              <p className="text-white font-medium text-sm truncate flex-1 mr-2">
                {property.title}
              </p>
              <p className="text-purple-400 font-semibold text-sm flex-shrink-0">
                {formatAmount(property.startPrice)}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 lg:p-6 space-y-4 flex-1 overflow-y-auto">
            {/* Fecha y hora de inicio */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                  <Calendar className="w-4 h-4" />
                  Fecha y hora de inicio
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={startNow}
                    onChange={(e) => setStartNow(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 bg-slate-700"
                  />
                  <span className="text-xs text-slate-400">Ahora</span>
                </label>
              </div>
              <input
                type="datetime-local"
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                disabled={startNow}
                className={`w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 lg:py-3 focus:outline-none focus:border-purple-500 ${
                  startNow ? 'text-slate-500 opacity-50 cursor-not-allowed' : 'text-white'
                }`}
              />
              <p className="text-xs text-slate-400">
                {startNow ? 'Se iniciará inmediatamente' : 'Cuándo comenzará el contador'}
              </p>
            </div>

            {/* Fecha y hora de cierre */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <Clock className="w-4 h-4" />
                Fecha y hora de cierre
              </label>
              <input
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 lg:py-3 text-white focus:outline-none focus:border-purple-500"
              />
              {/* Botones de duración rápida */}
              <div className="flex flex-wrap gap-2">
                {DURATION_OPTIONS.map((hours) => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => handleDurationClick(hours)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      getCurrentDuration() === hours
                        ? 'bg-purple-500 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    +{hours}h
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400">
                Duración actual: {getCurrentDuration()}h • Mínimo 12h recomendado
              </p>
            </div>

            {/* Lista de ofertas verificadas - compacta */}
            {verifiedOffers.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-400 font-medium">
                  Ofertas verificadas ({verifiedOffers.length}):
                </p>
                <div className="max-h-32 lg:max-h-40 overflow-y-auto space-y-1">
                  {verifiedOffers
                    .sort((a, b) => b.amount - a.amount || new Date(a.createdAt) - new Date(b.createdAt))
                    .map((offer) => (
                      <div
                        key={offer.id}
                        className="bg-slate-900/50 rounded px-2 py-1.5 flex items-center justify-between gap-2"
                      >
                        <p className="text-white text-sm truncate flex-1">{offer.buyerName}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <p className="text-white font-semibold text-sm">{formatAmount(offer.amount)}</p>
                          <SemaphoreBadge status={offer.semaphoreStatus} />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Info resumen */}
            <div className="text-xs lg:text-sm text-slate-400 bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 space-y-1">
              <p>✅ Cierre en {formatRemainingTime()}</p>
              <p>✅ Notificación a invitados habilitados</p>
              <p>✅ Modificación de ofertas permitida</p>
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 lg:p-6 border-t border-slate-700 flex-shrink-0 space-y-3">
            {/* Avisos de confirmación */}
            {showCloseWarning && (
              <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-2 text-red-300 text-xs text-center animate-pulse">
                Esto cerrará la fase sin activar el contador. Pulsa de nuevo para confirmar.
              </div>
            )}
            {showActivateWarning && (
              <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-2 text-amber-300 text-xs text-center animate-pulse">
                La duración es menor a 12 horas. Pulsa de nuevo para confirmar.
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={onCancel}
                className="flex-1 bg-slate-700 text-white font-medium py-2 lg:py-3 rounded-lg hover:bg-slate-600 transition-all text-sm lg:text-base"
              >
                Cancelar
              </button>
              <button
                onClick={handleClose}
                className={`flex-1 text-white font-medium py-2 lg:py-3 rounded-lg transition-all text-sm lg:text-base ${
                  showCloseWarning
                    ? 'bg-red-600 ring-2 ring-red-400'
                    : 'bg-red-600/80 hover:bg-red-600'
                }`}
              >
                {showCloseWarning ? 'Confirmar cerrar' : 'Cerrar ahora'}
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 text-white font-semibold py-2 lg:py-3 rounded-lg transition-all flex items-center justify-center gap-2 text-sm lg:text-base ${
                  showActivateWarning
                    ? 'bg-amber-500 ring-2 ring-amber-400'
                    : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                }`}
              >
                <Play className="w-4 h-4 lg:w-5 lg:h-5" />
                {showActivateWarning ? 'Confirmar activar' : 'Activar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateBlindActiveModal;
